import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  affectedJobConstants,
  customStylingSelectField,
  getAddMaterialFields,
  PriceUnits,
  selectWarnMessage,
  submitErrorWarning,
  SupplyStatusOptions,
  TaxStatusOptions,
  TestStatusOptions,
  WashedStatusOptions,
} from "./constants";
import styles from "./AddMaterialForm.module.scss";
import InputField from "../../../../../reusable-components/TNInputField";
import { Col, message, Modal, Row, Select } from "antd";
import AddPhotoDragger from "../AddPhotoDragger";
import { InputfieldTypes } from "../../../../../enums/InputFieldAttributesEnums";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../../../enums/ButtonAttributesEnums";
import TNButton from "../../../../../reusable-components/TNButton";
import QuarryDatabaseService from "../../../../../services/quarryDatabase.service";
import { useFormContext } from "react-hook-form";
import { FormFieldNames } from "../constants";
import { FileTypes } from "../../../../../enums/FileUploadEnums";
import TNLoader from "../../../../../reusable-components/TNLoader";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../../../constants/QueryKeys";
import ConditionalRender from "../../../../../reusable-components/ConditionalRender";
import { allowedKeys } from "../../../../../constants/keyboardKeys";
import { stripCurrencyFormatting } from "../../../../../Utilities/regex";
import { formatNumber } from "../../../../../utils/numberFormatterUtil";
import { bidToolResetModalConstants } from "../../../../BidToolConfigurationView/constants";

interface AddMaterialFormProps {
  handleOpenMaterialModal: () => void;
  isOpen: boolean;
  handleClose: () => void;
  quarryId: string;
  entryId?: string;
  isEdit: boolean;
  priceUnitDefault?: string;
  setSubCategoriesList?: React.Dispatch<React.SetStateAction<[]>>;
  setSelectedCategory?: React.Dispatch<React.SetStateAction<string>>;
  materialPictureProp?: [];
  materialDetails?: any;
  submitActionCall?: () => void;
}

const AddMaterialForm = forwardRef(
  (
    {
      handleOpenMaterialModal,
      isOpen,
      handleClose,
      quarryId,
      isEdit = false,
      priceUnitDefault,
      setSubCategoriesList,
      setSelectedCategory,
      materialPictureProp,
      materialDetails,
      entryId,
      submitActionCall,
    }: AddMaterialFormProps,
    ref,
  ) => {
    const { Option } = Select;
    const { LOAD_TXT, TON_TXT } = PriceUnits;
    const { reset, watch, getValues, setValue, handleSubmit, clearErrors } =
      useFormContext();
    const queryClient = useQueryClient();
    const categoryValue = watch(FormFieldNames.CATEGORY);
    const subCategoryValue = watch(FormFieldNames.SUB_CATEGORY);
    const materialId = watch(FormFieldNames.MATERIAL_NAME);
    const [affectedJobsCount, setAffectedJobsCount] = useState(0);
    const [oldMaterialCost, setOldMaterialCost] = useState(
      getValues(FormFieldNames.MATERIAL_PRICE),
    );
    const updatedMaterialCost = Number(
      stripCurrencyFormatting(watch(FormFieldNames.MATERIAL_PRICE)),
    );

    const isMaterialCostRevised = oldMaterialCost !== updatedMaterialCost;

    const {
      FetchMaterialCategoryMeta,
      FetchMaterialSubCategoryMeta,
      FetchMaterialNameMeta,
      AddNewQuarryMaterial,
      EditQuarryMaterial,
      GetAffectedJobsCount,
    } = QuarryDatabaseService();
    const { mutateAsync, isPending } = AddNewQuarryMaterial({
      quarryId: quarryId,
    });
    const { mutateAsync: editMaterialProfile, isPending: editPending } =
      EditQuarryMaterial({
        quarryId: quarryId,
        entryId: entryId,
      });
    const {
      mutateAsync: getAffectedJobsCount,
      isPending: isGetAffectedJobsCountPending,
    } = GetAffectedJobsCount();
    const { data: categories } = FetchMaterialCategoryMeta({
      enabled: isOpen,
    });
    const { data: subCategories, refetch: refetchSubCategories } =
      FetchMaterialSubCategoryMeta({
        categoryName: categoryValue ?? "",
        enabled: isOpen,
      });
    const { data: materials, refetch: refetchMaterials } =
      FetchMaterialNameMeta({
        categoryName: subCategoryValue ? (categoryValue ?? "") : "",
        subCategoryName: subCategoryValue ?? "",
        enabled: isOpen,
      });
    const [selectedPriceUnit, setSelectedPriceUnit] = useState<string>(TON_TXT);
    const [documentKey, setDocumentKey] = useState(undefined);
    const [documentMeta, setDocumentMeta] = useState({});
    const [fileAdded, setFileAdded] = useState<boolean>(false);

    const filteredMaterialData = materials?.filter(
      (item) => item?.id === getValues(FormFieldNames.MATERIAL_NAME),
    );
    const handlePriceUnitChange = (value: string) => {
      setSelectedPriceUnit(value);
    };

    const handleNumberEntry = (e) => {
      // Allow control and navigation keys
      if (
        allowedKeys?.includes(e?.key) ||
        e?.ctrlKey ||
        e?.altKey ||
        e?.metaKey
      ) {
        return;
      }

      // Allow digitls
      if (/^\d$/.test(e?.key)) {
        return;
      }

      // Allow decimal point if enabled
      if (e?.key === ".") {
        const currentValue = e?.target?.value?.toString() || "";
        if (currentValue.includes(".")) {
          e?.preventDefault();
          return;
        }
        return;
      }

      e?.preventDefault();
    };

    const handleMaterialPriceBlur = (e) => {
      if (e.target.value) {
        const stripped = stripCurrencyFormatting(e.target.value);
        const value = `$${formatNumber(Number(stripped).toFixed(2))}`;
        if (!isNaN(Number(stripped))) {
          setValue(
            FormFieldNames.MATERIAL_PRICE,
            `$${formatNumber(Number(stripped).toFixed(2))}`,
          );
        } else {
          setValue(FormFieldNames.MATERIAL_PRICE, null);
        }
        clearErrors(FormFieldNames.MATERIAL_PRICE);
      }
    };

    const handleSubmitNewMaterial = async () => {
      const payload = {
        ...filteredMaterialData?.[0],
        materialId: getValues(FormFieldNames.MATERIAL_NAME),
        category: getValues(FormFieldNames.CATEGORY),
        subCategory: getValues(FormFieldNames.SUB_CATEGORY),
        price: stripCurrencyFormatting(
          getValues(FormFieldNames.MATERIAL_PRICE),
        ),
        quantityType: selectedPriceUnit,
        materialPicture: fileAdded
          ? [
              {
                quoteAttachmentId: documentKey,
                ...documentMeta,
              },
            ]
          : [],
        materialNotes: getValues(FormFieldNames.DESCRIPTION),
        materialTax: getValues(FormFieldNames.MATERIAL_TAX),
        materialStatus:
          getValues(FormFieldNames.WASHED_STATUS) ?? WashedStatusOptions?.[2],
        materialSupply:
          getValues(FormFieldNames.SUPPLY_STATUS) ?? SupplyStatusOptions?.[4],
        materialTest:
          getValues(FormFieldNames.TESTING_STATUS) ?? TestStatusOptions?.[2],
      };
      delete payload?.id;
      delete payload?.indexVal;
      delete payload?.statusCode;
      delete payload?.material;
      delete payload?.createdAt;
      delete payload?.updatedAt;

      if (isEdit) {
        const editPayload = {
          ...materialDetails,
          ...payload,
          materialPicture: materialPictureProp,
          isMaterialCostRevised,
        };
        delete editPayload?.id;
        delete editPayload?.materialName;
        try {
          await editMaterialProfile(editPayload, {
            onSuccess: () => {
              reset();
              materialDetails?.category === categoryValue
                ? submitActionCall?.(categoryValue)
                : queryClient?.invalidateQueries([
                    QueryKeys?.FETCH_QUARRY_MATERIAL_CATEGORY_LIST_KEY,
                  ]);
              handleClose();
            },
          });
        } catch (error) {
          message.error(
            error?.response?.data?.message ||
              "Unable to update material details.",
          );
        }
      } else {
        try {
          await mutateAsync(payload);

          reset();
          queryClient.invalidateQueries([
            QueryKeys.FETCH_QUARRY_MATERIAL_CATEGORY_LIST_KEY,
          ]);
          handleClose();
        } catch (error) {
          message.error(
            error?.response?.data?.message || "Something went wrong.",
          );
        }
      }
    };

    const handleSubmitErrors = (errors) => {
      console.error(`${submitErrorWarning}`, errors);
    };

    const handleCloseAffectedModal = () => {
      setAffectedJobsCount(0);
    };

    const handleConfirmPriceChange = () => {
      handleSubmitNewMaterial();
      handleCloseAffectedModal();
    };

    const handleMaterialSubmit = async () => {
      if (isEdit && isMaterialCostRevised) {
        try {
          const countResponse = await getAffectedJobsCount({
            quarryId,
            revisedMaterialCost: stripCurrencyFormatting(
              getValues(FormFieldNames.MATERIAL_PRICE),
            ),
            materialValue: filteredMaterialData[0]?.materialValue,
            materialType: materialDetails?.quantityType,
          });

          const ordersAffectedJobsCount = countResponse?.data?.count;

          if (ordersAffectedJobsCount > 0) {
            setAffectedJobsCount(ordersAffectedJobsCount);
            return;
          }
        } catch (error) {
          message.error("Unable to check affected jobs. Please try again.");
          return;
        }
      }

      return handleSubmitNewMaterial();
    };

    useImperativeHandle(ref, () => ({
      handleSubmitForm: handleSubmit(handleMaterialSubmit, handleSubmitErrors),
    }));

    const selectPriceUnitAfterFieldProps = (
      <Select
        value={selectedPriceUnit}
        onChange={handlePriceUnitChange}
        className={styles.selectDropdown}
        disabled={isEdit}
      >
        <Option value={TON_TXT}>
          {<span className={styles.unitContainer}>{TON_TXT}</span>}
        </Option>
        <Option value={LOAD_TXT}>
          {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
        </Option>
      </Select>
    );

    const fieldsList = getAddMaterialFields({
      selectPriceUnitAfter: selectPriceUnitAfterFieldProps,
      handleMaterialModalOpen: () => {
        if (!!subCategoryValue && !!categoryValue) handleOpenMaterialModal();
        else message.warn(selectWarnMessage);
      },
      categories: categories,
      subCategories:
        subCategories?.map((item) => ({
          label: item?.subCategory,
          value: item?.subCategory,
        })) ?? [],
      materials:
        materials?.map((item) => ({
          label: item?.material,
          value: item?.id,
        })) ?? [],
    });

    useEffect(() => {
      if (isEdit) {
        setSelectedPriceUnit(priceUnitDefault);
        setValue(FormFieldNames?.CATEGORY, materialDetails?.category);
        setValue(FormFieldNames?.SUB_CATEGORY, materialDetails?.subCategory);
        setValue(FormFieldNames?.MATERIAL_NAME, materialDetails?.materialId);
        setValue(FormFieldNames?.DESCRIPTION, materialDetails?.materialNotes);
        setValue(
          FormFieldNames?.MATERIAL_PRICE,
          `$${formatNumber(Number(materialDetails?.price)?.toFixed(2))}`,
        );
        setValue(FormFieldNames?.MATERIAL_TAX, materialDetails?.materialTax);
        setValue(FormFieldNames?.TESTING_STATUS, materialDetails?.materialTest);
        setValue(
          FormFieldNames?.WASHED_STATUS,
          materialDetails?.materialStatus,
        );
        setValue(
          FormFieldNames?.SUPPLY_STATUS,
          materialDetails?.materialSupply,
        );
        setOldMaterialCost(Number(materialDetails?.price));
      } else {
        setValue(FormFieldNames?.MATERIAL_TAX, TaxStatusOptions[1]);
      }
    }, [isEdit]);

    useEffect(() => {
      setSubCategoriesList(subCategories);
    }, [subCategories]);

    useEffect(() => {
      setSelectedCategory(categoryValue);
    }, [categoryValue]);

    return (
      <>
        <TNLoader
          isLoading={isPending || editPending || isGetAffectedJobsCountPending}
        />
        <Modal
          visible={affectedJobsCount > 0}
          onCancel={handleCloseAffectedModal}
          title={affectedJobConstants.MODAL_TITLE}
          okText={bidToolResetModalConstants.CONFIRM_TEXT}
          onOk={handleConfirmPriceChange}
        >
          <p>
            {affectedJobConstants.MODAL_CONTENT_BEFORE_COUNT}
            <span className={styles.warningText}>
              {` ${affectedJobsCount} ${affectedJobConstants.MODAL_CONTENT_AFTER_COUNT}`}
            </span>
          </p>
        </Modal>
        <div className={styles.inputFieldsWrapper}>
          <Row>
            {fieldsList?.map((item) => (
              <Col span={item.span}>
                <InputField
                  type={item.type}
                  name={item.name}
                  label={item.label}
                  requiredField={item.required}
                  options={item.options}
                  placeholder={item.placeholder}
                  maxLength={item.maxLength}
                  noResize={item.noResize}
                  addonAfter={item.addOnAfter}
                  prefix={item.prefix}
                  allowClear={true}
                  {...(item.type === InputfieldTypes.SELECT_CUSTOM_FIELD
                    ? {
                        dropdownSelectRender: (
                          <>
                            <TNButton
                              text={item.customButtonLabel}
                              type={ButtonType.PRIMARY}
                              size={ButtonSize.LARGE}
                              onClick={item.handleModalOpen}
                              customStyles={customStylingSelectField}
                              shape={ButtonShape.DEFAULT}
                            />
                          </>
                        ),
                        onChange: (value) => {
                          setValue(item.name, value);
                        },
                      }
                    : {})}
                  {...(item.name === FormFieldNames.CATEGORY
                    ? {
                        changeHandler: () => {
                          setValue(FormFieldNames.SUB_CATEGORY, "");
                          setValue(FormFieldNames.MATERIAL_NAME, "");
                        },
                      }
                    : item.name === FormFieldNames.SUB_CATEGORY
                      ? {
                          changeHandler: () => {
                            setValue(FormFieldNames.MATERIAL_NAME, "");
                          },
                        }
                      : {})}
                  {...(item.name === FormFieldNames.MATERIAL_PRICE
                    ? {
                        onKeyDown: handleNumberEntry,
                        onBlur: handleMaterialPriceBlur,
                      }
                    : {})}
                />
              </Col>
            ))}
            <ConditionalRender visible={!isEdit}>
              <Col span={24}>
                <AddPhotoDragger
                  docType={""}
                  quarryId={quarryId}
                  materialId={materialId}
                  filesUploaded={[]}
                  maxFileSize={1}
                  setDocumentKey={setDocumentKey}
                  setDocumentMeta={setDocumentMeta}
                  disabled={!!!materialId}
                  setFileAdded={setFileAdded}
                />
              </Col>
            </ConditionalRender>
          </Row>
        </div>
      </>
    );
  },
);

export default AddMaterialForm;
